import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Send } from 'lucide-react';
import { getOrCreateProposalToken } from '../lib/proposals';

interface ProposalEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (subject: string, message: string) => Promise<void>;
  proposalTitle: string;
  businessName: string;
  customerEmail: string;
  customerFirstName: string;
  proposalId: string;
  businessId: string;
}

export default function ProposalEmailModal({
  isOpen,
  onClose,
  onSend,
  proposalTitle,
  businessName,
  customerEmail,
  customerFirstName,
  proposalId,
  businessId
}: ProposalEmailModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [proposalUrl, setProposalUrl] = useState('');

  // Set default values when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      
      // Generate proposal URL
      const generateUrl = async () => {
        try {
          const tokenData = await getOrCreateProposalToken(proposalId, businessId);
          const url = `${window.location.origin}/proposal/${tokenData}`;
          setProposalUrl(url);
          
          // Always reset to default message (not edited version)
          setSubject(`New Proposal: ${proposalTitle}`);
          setMessage(`Hey ${customerFirstName}, please use the link below to view the proposal details\n\n${url}`);
        } catch (error) {
          console.error('Error generating proposal URL:', error);
          // Fallback message without URL
          setSubject(`New Proposal: ${proposalTitle}`);
          setMessage(`Hey ${customerFirstName}, please use the link below to view the proposal details`);
        }
      };
      
      generateUrl();
    }
  }, [isOpen, proposalTitle, businessName, customerFirstName, proposalId, businessId]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSubject('');
      setMessage('');
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return;

    try {
      setLoading(true);
      await onSend(subject, message);
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100040]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <div className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-white" />
                  <h2 className="text-xl font-semibold text-white">
                    Send Proposal Email
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="space-y-0">
              {/* Recipient */}
              <div className="bg-white border-b border-gray-200 p-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  To
                </label>
                <div className="px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                  {customerEmail}
                </div>
              </div>

              {/* Subject */}
              <div className="bg-white border-b border-gray-200 p-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter email subject"
                />
              </div>

              {/* Message */}
              <div className="bg-white p-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Message
                </label>
                <textarea
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder="Enter your message"
                />
                <p className="text-xs text-gray-500 mt-2">
                  You can edit this message for this send only. The default message will be restored next time.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={loading || !subject.trim() || !message.trim()}
                className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  (!loading && subject.trim() && message.trim())
                    ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500' 
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
